import { EMPTY, from, ignoreElements, Observable } from 'rxjs';
import { captureException } from '../../tracing';
import { noResponseCameBack } from './reportable-error.util';

/**
 * Ends a stream that failed: the real error is reported, nothing is emitted,
 * and the screen carries on.
 *
 * WHAT THIS REPLACES, AND WHY IT MATTERS MORE THAN IT LOOKS. Seven handlers in
 * the groups state wrote
 *
 *   catchError((err) => { console.log(err); return err; })
 *
 * `catchError` must return a STREAM. Given an HttpErrorResponse, an ordinary
 * object, RxJS cannot convert it and throws
 *
 *   TypeError: You provided an invalid object where a stream was expected.
 *
 * That TypeError then REPLACES the failure it was meant to handle. The 500, the
 * timeout, the 403, whatever actually went wrong, is gone: what reaches Sentry
 * is a stack of minified vendor frames that names no call site and is identical
 * for every one of the seven. CAPACITOR-X is exactly that, raised on
 * /home/group/:id when the group failed to load, and it says nothing about the
 * group or the load.
 *
 * The `async` variants of the same mistake do not throw, because a Promise IS
 * something RxJS accepts. They are worse in a quieter way: the error resolves
 * as a VALUE, so the stream succeeds and the failure is reported nowhere at
 * all. Both shapes are replaced by this.
 *
 * WHY EMPTY AND NOT A RETHROW. These handlers already decided to absorb the
 * failure, and the screens around them are built on that: they show a toast and
 * stay where they are. Rethrowing would change what a member sees on a bad
 * connection, which is a separate decision from making the error reportable.
 * The stream completes without emitting, so a `map` downstream never runs on a
 * value that does not exist.
 *
 * NO RESPONSE IS NOT REPORTED. A call that got no answer at all, status 0, is
 * the phone losing its signal, not a bug: CAPACITOR-1B was a group page on a
 * weak 4G connection, and the API never saw the request. The member still gets
 * the toast and the screen still carries on; only the report is left out.
 */
export const aFailedCallIsReportedAndStops = <T = never>(
  error: unknown,
  /**
   * What to do before the stream ends, such as showing a toast. Optional, and
   * its own failure is swallowed: a toast that cannot be shown is not a second
   * thing to report, and letting it error here would put us back where we
   * started, with the original failure replaced by a later one.
   */
  after?: () => Promise<unknown>,
): Observable<T> => {
  if (!noResponseCameBack(error)) {
    captureException(error);
  }

  if (!after) {
    return EMPTY;
  }

  const andThenStop = async (): Promise<void> => {
    try {
      await after();
    } catch {
      // Deliberately silent. See the parameter comment above.
    }
  };

  return from(andThenStop()).pipe(ignoreElements());
};
